import type { ElementType, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/cn';

interface FadeInSectionProps {
  children: ReactNode;
  className?: string;
  /** Delay in ms before animation starts after becoming visible */
  delay?: number;
  /** Element tag to render */
  as?: ElementType;
}

/**
 * Wrapper that fades in its children when they scroll into view.
 * Uses Intersection Observer for performance.
 */
export const FadeInSection = ({
  children,
  className,
  delay = 0,
  as: Component = 'div',
}: FadeInSectionProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Once visible, stay visible (no re-hide on scroll up)
          if (delay > 0) {
            timeoutRef.current = setTimeout(() => setIsVisible(true), delay);
          } else {
            setIsVisible(true);
          }
          observer.unobserve(element);
        }
      },
      {
        threshold: 0.1, // Trigger when 10% visible
        rootMargin: '0px 0px -50px 0px', // Trigger slightly before fully in view
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [delay]);

  return (
    <Component
      ref={ref}
      className={cn(
        // Respect reduced motion preference
        'motion-safe:transition-all motion-safe:duration-700 motion-safe:ease-out',
        isVisible
          ? 'opacity-100 motion-safe:translate-y-0'
          : 'motion-safe:opacity-0 motion-safe:translate-y-4 motion-reduce:opacity-100',
        className
      )}
    >
      {children}
    </Component>
  );
};
